import { Alert, Badge, Button, Field, Input, Skeleton } from '@personachat/ui';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, ImageUp, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { ErreurChargement, InfoList, Section, Select } from '../../components/bits';
import { Drawer } from '../../components/Drawer';
import { ConfirmDialog } from '../../components/Modal';
import { TabPanel, Tabs } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { formatDateTime, formatNombre } from '../../lib/format';
import { AuditTimeline } from '../audit/AuditTimeline';

import { LIBELLE_SOURCE, useCatalogue, useCoach, useEcritureContenus, type Coach } from './catalogue';
import { Avatar } from './CoachsPage';

type Reglages = components['schemas']['CoachWrite'];
type Onglet = 'reglages' | 'prompt' | 'journal';

/** Fiche d'un coach (cahier des charges §7.7) : réglages, avatar, prompt, journal. */
export function CoachDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const coach = useCoach(id);
  const [onglet, setOnglet] = useState<Onglet>('reglages');
  const c = coach.data;

  return (
    <Drawer
      onClose={() => navigate('/coachs')}
      label={c ? `Fiche du coach ${c.name}` : 'Fiche coach'}
      title={c ? c.name : <Skeleton width={220} height={22} />}
      subtitle={c ? `Module « ${c.module.name} »` : undefined}
      badges={c ? <Badge tone={c.isActive ? 'success' : 'neutral'}>{c.isActive ? 'Actif' : 'Inactif'}</Badge> : undefined}
    >
      {coach.isError ? (
        <div style={{ paddingTop: 16 }}>
          <ErreurChargement erreur={coach.error} onReessayer={() => void coach.refetch()} />
        </div>
      ) : !c ? (
        <div className="stack" style={{ paddingTop: 16 }}>
          <Skeleton height={36} />
          <Skeleton height={220} />
        </div>
      ) : (
        <>
          <Tabs
            label="Sections de la fiche"
            onglets={[
              { id: 'reglages', label: 'Réglages' },
              { id: 'prompt', label: 'Prompt', count: c._count.promptVersions },
              { id: 'journal', label: 'Journal' },
            ]}
            actif={onglet}
            onChange={setOnglet}
          />
          <TabPanel label={onglet === 'reglages' ? 'Réglages' : onglet === 'prompt' ? 'Prompt' : 'Journal'}>
            {onglet === 'reglages' && <ReglagesCoach key={c.updatedAt} coach={c} />}
            {onglet === 'prompt' && <ResumePrompt coach={c} />}
            {onglet === 'journal' && <AuditTimeline targetId={c.id} />}
          </TabPanel>
        </>
      )}
    </Drawer>
  );
}

function ResumePrompt({ coach }: { coach: Coach }) {
  const v = coach.currentVersion;
  return (
    <div className="stack">
      <InfoList
        items={[
          { label: 'Longueur', value: `${formatNombre(coach.systemPrompt.length)} caractères` },
          { label: 'Versions', value: formatNombre(coach._count.promptVersions) },
          { label: 'En service depuis', value: v ? formatDateTime(v.createdAt) : '—' },
          { label: 'Publiée par', value: v ? `${v.authorEmail ?? 'inconnu'} (${LIBELLE_SOURCE[v.source]})` : '—' },
        ]}
      />
      <pre className="apercu-prompt">{coach.systemPrompt.slice(0, 1200)}{coach.systemPrompt.length > 1200 ? '…' : ''}</pre>
      <div>
        <Link className="ui-btn ui-btn--primary lien-bouton" to={`/prompts/${coach.id}`}>
          <FileText size={15} aria-hidden="true" /> Ouvrir l’éditeur de prompt
        </Link>
      </div>
    </div>
  );
}

/** Réglages du coach ; seuls les champs changés partent à l'API. */
function ReglagesCoach({ coach }: { coach: Coach }) {
  const id = useId();
  const toast = useToast();
  const client = useQueryClient();
  const ecriture = useEcritureContenus();
  const catalogue = useCatalogue();
  const initial: Required<Reglages> = {
    name: coach.name,
    description: coach.description,
    welcomeMessage: coach.welcomeMessage ?? '',
    moduleId: coach.moduleId,
    isActive: coach.isActive,
    canGenerateImages: coach.canGenerateImages,
    canUseMemory: coach.canUseMemory,
    canEnrichMemory: coach.canEnrichMemory,
    questionsCount: coach.questionsCount,
    tutorialVideoUrl: coach.tutorialVideoUrl ?? '',
    tutorialVideoTitle: coach.tutorialVideoTitle ?? '',
    aiModel: coach.aiModel,
    reasoningEffort: (coach.reasoningEffort as Required<Reglages>['reasoningEffort']) ?? 'medium',
  };
  const [v, setV] = useState(initial);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [confirmerModule, setConfirmerModule] = useState(false);

  const changes = (Object.keys(initial) as Array<keyof Reglages>).filter((k) => v[k] !== initial[k]);
  const maj = <K extends keyof Reglages>(k: K, valeur: Required<Reglages>[K]) => setV((a) => ({ ...a, [k]: valeur }));

  const envoyer = async () => {
    setEnvoi(true);
    setErreur(null);
    const body: Reglages = {};
    for (const k of changes) {
      const valeur = v[k];
      // Champs facultatifs vidés : null côté API.
      (body as Record<string, unknown>)[k] =
        (k === 'welcomeMessage' || k === 'tutorialVideoUrl' || k === 'tutorialVideoTitle') && typeof valeur === 'string' && !valeur.trim() ? null : valeur;
    }
    try {
      await lire(api.PATCH('/api/admin/catalog/coaches/{id}', { params: { path: { id: coach.id } }, body }));
      toast.succes('Réglages du coach enregistrés.');
      void client.invalidateQueries({ queryKey: ['coach', coach.id] });
      void client.invalidateQueries({ queryKey: ['catalogue'] });
      void client.invalidateQueries({ queryKey: ['audit', coach.id] });
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    if (!changes.length || envoi) return;
    if (changes.includes('moduleId')) setConfirmerModule(true);
    else void envoyer();
  };

  const modules = catalogue.data ?? [];
  const nouveauModule = modules.find((m) => m.id === v.moduleId)?.name;

  return (
    <form className="stack" onSubmit={soumettre}>
      {!ecriture && <Alert tone="info">Consultation : seul un super admin modifie un coach.</Alert>}
      <AvatarCoach coach={coach} ecriture={ecriture} />
      {erreur && <Alert>{erreur}</Alert>}
      <fieldset className="fieldset-nu stack" disabled={!ecriture}>
        <div className="grille-2">
          <Field id={`${id}-nom`} label="Nom">
            <Input id={`${id}-nom`} value={v.name} onChange={(e) => maj('name', e.target.value)} maxLength={80} />
          </Field>
          <Field id={`${id}-module`} label="Module">
            <Select id={`${id}-module`} value={v.moduleId} onChange={(e) => maj('moduleId', e.target.value)}>
              {modules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field id={`${id}-desc`} label="Description" hint="Affichée aux élèves sous le nom du coach.">
          <textarea id={`${id}-desc`} className="ui-input textarea textarea--texte" rows={2} value={v.description} onChange={(e) => maj('description', e.target.value)} maxLength={1000} />
        </Field>
        <Field id={`${id}-accueil`} label="Message d’accueil" hint="Premier message affiché dans une nouvelle conversation (facultatif).">
          <textarea id={`${id}-accueil`} className="ui-input textarea textarea--texte" rows={3} value={v.welcomeMessage ?? ''} onChange={(e) => maj('welcomeMessage', e.target.value)} maxLength={4000} />
        </Field>
        <fieldset className="choix-cases">
          <legend className="ui-field__label">Capacités</legend>
          <label className="case">
            <input type="checkbox" checked={v.isActive} onChange={(e) => maj('isActive', e.target.checked)} />
            <span>
              <strong>Actif</strong> : visible par les élèves qui y ont accès
            </span>
          </label>
          <label className="case">
            <input type="checkbox" checked={v.canGenerateImages} onChange={(e) => maj('canGenerateImages', e.target.checked)} />
            Peut générer des images
          </label>
          <label className="case">
            <input type="checkbox" checked={v.canUseMemory} onChange={(e) => maj('canUseMemory', e.target.checked)} />
            Lit la mémoire de l’élève
          </label>
          <label className="case">
            <input type="checkbox" checked={v.canEnrichMemory} onChange={(e) => maj('canEnrichMemory', e.target.checked)} />
            Complète la mémoire de l’élève
          </label>
        </fieldset>
        <div className="grille-2">
          <Field id={`${id}-questions`} label="Questions du parcours" hint="Nombre de questions affichées à l’élève.">
            <Input
              id={`${id}-questions`}
              type="number"
              min={0}
              max={200}
              value={String(v.questionsCount)}
              onChange={(e) => maj('questionsCount', Math.max(0, Math.min(200, Math.round(Number(e.target.value) || 0))))}
            />
          </Field>
          <Field id={`${id}-video-titre`} label="Titre du tutoriel vidéo">
            <Input id={`${id}-video-titre`} value={v.tutorialVideoTitle ?? ''} onChange={(e) => maj('tutorialVideoTitle', e.target.value)} maxLength={120} />
          </Field>
        </div>
        <Field id={`${id}-video`} label="Lien du tutoriel vidéo" hint="Adresse en https://, facultative.">
          <Input id={`${id}-video`} type="url" value={v.tutorialVideoUrl ?? ''} onChange={(e) => maj('tutorialVideoUrl', e.target.value)} maxLength={500} placeholder="https://" />
        </Field>
        <details className="avance">
          <summary>Réglages avancés du modèle</summary>
          <div className="grille-2" style={{ marginTop: 10 }}>
            <Field id={`${id}-modele`} label="Modèle" hint="À changer seulement à la demande de l’équipe technique.">
              <Input id={`${id}-modele`} value={v.aiModel} onChange={(e) => maj('aiModel', e.target.value)} maxLength={80} className="mono-sm" />
            </Field>
            <Field id={`${id}-raisonnement`} label="Effort de raisonnement">
              <Select id={`${id}-raisonnement`} value={v.reasoningEffort} onChange={(e) => maj('reasoningEffort', e.target.value as Required<Reglages>['reasoningEffort'])}>
                <option value="minimal">Minimal</option>
                <option value="low">Faible</option>
                <option value="medium">Moyen</option>
                <option value="high">Élevé</option>
              </Select>
            </Field>
          </div>
        </details>
      </fieldset>
      <InfoList
        items={[
          { label: 'Conversations', value: formatNombre(coach._count.conversations) },
          { label: 'Élèves avec accès débloqué', value: formatNombre(coach._count.unlockedByUsers) },
          { label: 'Clés de déblocage', value: formatNombre(coach._count.unlockKeys) },
          { label: 'Créé le', value: formatDateTime(coach.createdAt) },
        ]}
      />
      {ecriture && (
        <div className="barre-enregistrer">
          <span className="muted small">{changes.length ? `${changes.length} modification${changes.length > 1 ? 's' : ''} non enregistrée${changes.length > 1 ? 's' : ''}` : 'À jour'}</span>
          <Button onClick={() => setV(initial)} disabled={!changes.length || envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={!changes.length}>
            Enregistrer
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirmerModule}
        onOpenChange={setConfirmerModule}
        title="Changer le coach de module"
        confirmLabel="Déplacer et enregistrer"
        onConfirm={envoyer}
      >
        <p>
          Le coach passe dans le module « {nouveauModule} », en dernière position. S’il faisait partie du calendrier d’un événement, il en est
          retiré.
        </p>
      </ConfirmDialog>
    </form>
  );
}

/** Avatar : image téléversée (PNG, JPEG, WebP, 2 Mo au plus) ou initiales. */
function AvatarCoach({ coach, ecriture }: { coach: Coach; ecriture: boolean }) {
  const toast = useToast();
  const client = useQueryClient();
  const fichier = useRef<HTMLInputElement>(null);
  const [envoi, setEnvoi] = useState(false);
  const [retirer, setRetirer] = useState(false);

  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: ['coach', coach.id] });
    void client.invalidateQueries({ queryKey: ['catalogue'] });
  };

  const televerser = async (f: File) => {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
      toast.erreur('Format accepté : PNG, JPEG ou WebP.');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.erreur('Image trop lourde : 2 Mo au plus.');
      return;
    }
    setEnvoi(true);
    try {
      const donnees = new FormData();
      donnees.append('file', f);
      await lire(
        api.POST('/api/admin/catalog/coaches/{id}/avatar', {
          params: { path: { id: coach.id } },
          // FormData : openapi-fetch laisse le navigateur poser l'en-tête multipart.
          body: donnees as unknown as { file: string },
        })
      );
      toast.succes('Avatar enregistré.');
      rafraichir();
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setEnvoi(false);
      if (fichier.current) fichier.current.value = '';
    }
  };

  return (
    <Section title="Avatar">
      <div className="avatar-bloc">
        <Avatar nom={coach.name} url={coach.avatar} taille={72} />
        {ecriture && (
          <div className="stack" style={{ gap: 6 }}>
            <input
              ref={fichier}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              id={`avatar-${coach.id}`}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void televerser(f);
              }}
            />
            <div className="row">
              <Button size="sm" icon={<ImageUp size={14} aria-hidden="true" />} loading={envoi} onClick={() => fichier.current?.click()}>
                {coach.hasUploadedAvatar ? 'Remplacer' : 'Téléverser une image'}
              </Button>
              {coach.hasUploadedAvatar && (
                <Button size="sm" variant="ghost" icon={<Trash2 size={14} aria-hidden="true" />} onClick={() => setRetirer(true)}>
                  Retirer
                </Button>
              )}
            </div>
            <span className="muted small">Carré de préférence, PNG, JPEG ou WebP, 2 Mo au plus.</span>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={retirer}
        onOpenChange={setRetirer}
        title="Retirer l’avatar"
        confirmLabel="Retirer"
        onConfirm={async () => {
          await lire(api.DELETE('/api/admin/catalog/coaches/{id}/avatar', { params: { path: { id: coach.id } } }));
          toast.succes('Avatar retiré.');
          rafraichir();
        }}
      >
        <p>Les élèves verront de nouveau l’avatar par défaut du coach.</p>
      </ConfirmDialog>
    </Section>
  );
}
